import type { Field } from '../../api/services'
import { currentApiEnvironment } from './environments'
import { exampleFor, isMatchContract } from './contract'

/**
 * The Python example shown on a service's page.
 *
 * Built from the capability's own contract rather than from a table of
 * snippets: the resource and the method are the two halves of the operation
 * code, and the keyword arguments are the request fields the server just
 * described. A capability the platform gains tomorrow gets a correct example
 * without anybody editing this file, and — more to the point — a capability
 * the platform *removes* cannot leave a stale snippet behind advertising it.
 *
 * Every credential and every input is obviously fake. A copyable example that
 * runs as written against real data is a bill somebody did not intend.
 */

/**
 * Placeholder credentials, and the one value in here that is real.
 *
 * The credentials are fake and visibly fake. `baseUrl` is not: an example
 * whose credentials are obviously placeholders but whose host is also wrong
 * fails twice, and the second failure looks like the platform being down
 * rather than like a snippet needing to be filled in. It names the Stage base,
 * because Stage is where these credentials can be issued today.
 */
export const DEMO = {
  clientId: 'ap_demo_0000000000000000',
  clientSecret: 'aps_demo_not_a_real_secret_0000',
  baseUrl: currentApiEnvironment().baseUrl,
  package: 'api-platform',
  index: 'https://pypi.office.lan/internal/prod/+simple/',
} as const

/** A field's example value written as a Python literal. */
function literal(field: Field): string {
  const value = exampleFor(field)
  if (typeof value === 'boolean') return value ? 'True' : 'False'
  if (field.type === 'integer' || field.type === 'number') return value
  return `"${value}"`
}

const PREAMBLE = [
  'from api_platform import Client',
  '',
  'client = Client(',
  `    client_id="${DEMO.clientId}",`,
  `    client_secret="${DEMO.clientSecret}",`,
  `    base_url="${DEMO.baseUrl}",`,
  ')',
  '',
].join('\n')

/**
 * A runnable-shaped call for one capability.
 *
 * `idempotency_key` is in every example on purpose. The SDK generates one when
 * it is omitted, but a key that does not survive a process restart does not
 * protect a retried job from being billed twice — so the example shows a key
 * derived from the caller's own unit of work.
 */
export function pythonExample(
  code: string,
  requestFields: readonly Field[],
  resultFields: readonly Field[],
): string {
  const parts = code.split('.')
  const args = requestFields
    .map((field) => `    ${field.name}=${literal(field)},`)
    .concat('    idempotency_key="order-4711",')
    .join('\n')

  // A matching capability's answer is a boolean, and the mistake this guards
  // against is written in code rather than in prose: treating `matched is
  // False` as a failure and retrying it pays twice for the same answer.
  const tail = isMatchContract(resultFields)
    ? [
        'if result.matched:',
        '    ...  # the identifiers belong together',
        'else:',
        '    ...  # a successful, billed answer meaning "no". Do not retry it.',
        '',
      ].join('\n')
    : 'print(result)\n'

  if (parts.length !== 2 || !parts[0] || !parts[1]) {
    // Not a resource.method code. Rather than guess at an attribute path that
    // does not exist, fall back to the low-level request the SDK also exposes.
    return `${PREAMBLE}result = client.request(\n    "POST",\n    "/api/v1/operations/${code}/",\n    json={},\n    idempotency_key="order-4711",\n)\n${tail}`
  }

  return `${PREAMBLE}result = client.${parts[0]}.${parts[1]}(\n${args}\n)\n${tail}`
}
